import api from './client';
import { Bird } from './birds';
import { EggStatus, ChickRearMethod } from '../utils/enums';
import { asArray } from '../utils/asArray';

export interface Egg {
  id: string;
  status: EggStatus;
  layDate: string;
  clutchId?: string;
  incubationStartDate: string | null;
  incubationDays: number | null;
  bandingNotificationDays: number | null;
  comment: string | null;
  chickRearMethod?: ChickRearMethod | null;
  hatchingDate: string | null;
  bandingDate: string | null;
  bandNumber: string | null;
  failureReason: string | null;
}

export interface Clutch {
  id: string;
  date: string;
  breedingPairId: string;
  eggs: Egg[];
  createdAt?: string;
  updatedAt?: string;
  daysRemainingTillHatching?: number | null;
}

export interface BreedingPair {
  id: string;
  maleBirdId: string;
  femaleBirdId: string;
  maleBirdEntity?: Bird;
  femaleBirdEntity?: Bird;
  pairingBeginDate?: string;
  pairingEndDate?: string;
  cageNumber: string;
  nestNumber?: string;
  clutches: Clutch[];
  createdAt?: string;
  updatedAt?: string;
  comments?: string;
  allowHybridization?: boolean;
}

export const breedingApi = {
  getAll: async () => {
    const response = await api.get<BreedingPair[] | { items: BreedingPair[] }>('breeding/pairs');
    return { ...response, data: asArray<BreedingPair>(response.data) };
  },
  getById: (id: string) => api.get<BreedingPair>(`breeding/pairs/${id}`),
  create: (data: CreateBreedingPairDto) => api.post<BreedingPair>('breeding/pairs', data),
  update: (id: string, data: Partial<BreedingPair>) => api.patch<BreedingPair>(`breeding/pairs/${id}`, data),
  delete: (id: string) => api.delete(`breeding/pairs/${id}`),
  addClutch: (pairId: string, data: CreateClutchDto) => 
    api.post<Clutch>(`breeding/pairs/${pairId}/clutches`, data),
  updateClutch: (pairId: string, clutchId: string, data: Partial<Clutch>) => 
    api.patch<BreedingPair>(`breeding/pairs/${pairId}/clutches/${clutchId}`, data),
  removeClutch: (pairId: string, clutchId: string) => 
    api.delete<BreedingPair>(`breeding/pairs/${pairId}/clutches/${clutchId}`),
};

export const eggsApi = {
  getByClutchId: (clutchId: string) => api.get<Egg[]>(`breeding/clutches/${clutchId}/eggs`),
  create: (clutchId: string, data: CreateEggDto) => api.post<Egg>(`breeding/clutches/${clutchId}/eggs`, data),
  update: (clutchId: string, eggId: string, data: Partial<Egg>) =>
    api.patch<Egg>(`breeding/clutches/${clutchId}/eggs/${eggId}`, data),
  delete: (id: string) => api.delete(`breeding/eggs/${id}`),
};

export interface CreateBreedingPairDto {
  maleBirdId: string;
  femaleBirdId: string;
  pairingBeginDate?: string;
  cageNumber?: string;
  allowHybridization?: boolean;
  notes?: string;
}

export interface CreateClutchDto {
  date: string;
  status?: string;
  notes?: string;
  metadata?: {
    incubationNotes?: string;
    hatchingNotes?: string;
    bandingNotes?: string;
    healthChecks?: {
      date: Date;
      status: 'healthy' | 'concern' | 'issue';
      notes?: string;
    }[];
  };
}

export interface CreateEggDto {
  id: string;
  clutchId?: string;
  status: EggStatus;
  layDate: string;
  chickRearMethod: ChickRearMethod | null;
  incubationStartDate: string | null;
  incubationDays: number | null;
  bandingNotificationDays: number | null;
  comment: string | null;
  hatchingDate: string | null;
  bandingDate: string | null;
  bandNumber: string | null;
  failureReason: string | null;
} 